import * as React from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Box from '@cloudscape-design/components/box';
import Button from '@cloudscape-design/components/button';
import Header from '@cloudscape-design/components/header';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Table from '@cloudscape-design/components/table';
import type { DailyAggregate } from '@etiquetachef/types';
import { formatInt, formatUsdCents, formatYyyymmddDisplay } from './formatters';

type SortKey = 'mrr' | 'sales';

const TOP_LIMIT = 20;

/**
 * Tabela "Top 20 dias" do `/dashboard/finance`, ordenável por MRR (USD eq.) ou por vendas
 * (tenants criados no dia), com o net new MRR de cada dia.
 *
 * Origem: sem issue — commit 498e432c6, 2026-05-04 15:52 +0200 (rota /dashboard/finance).
 *
 * Contrato: só apresentação — ordena em memória a `series` de `DailyAggregate` do período
 * (desc) e corta em `TOP_LIMIT = 20`. Lê `mrr.usdEquivalentCents`, `tenants.createdToday` e
 * `netNewMrr.netUsdCents` (CENTAVOS, formatados por `formatUsdCents`).
 *
 * Usado por: `FinanceDashboardPage` (web `apps/admin`, super-admin).
 *
 * Dados: `/aggregations/daily/{yyyymmdd}` — `DailyAggregateSchema` em
 * `packages/types/src/aggregations.ts`; escrito por `aggregateDailyMetrics` (Functions).
 *
 * Histórico:
 *  - 2026-06-08 23:55 +0200 — (39-G) migração para Cloudscape `Table`/`Button`.
 */
export function TopDaysTable({
  series,
}: {
  series: readonly DailyAggregate[];
}): React.JSX.Element {
  const { t } = useTranslation();
  const [sortKey, setSortKey] = useState<SortKey>('mrr');

  const sorted = [...series]
    .sort((a, b) => {
      const av = sortKey === 'mrr' ? a.mrr.usdEquivalentCents : a.tenants.createdToday;
      const bv = sortKey === 'mrr' ? b.mrr.usdEquivalentCents : b.tenants.createdToday;
      return bv - av;
    })
    .slice(0, TOP_LIMIT);

  return (
    <Table<DailyAggregate>
      items={sorted}
      trackBy="yyyymmdd"
      variant="container"
      empty={<Box textAlign="center" color="inherit">{t('finance.empty')}</Box>}
      header={
        <Header
          variant="h3"
          actions={
            <SpaceBetween direction="horizontal" size="xs">
              <Button
                variant={sortKey === 'mrr' ? 'primary' : 'normal'}
                onClick={(): void => {
                  setSortKey('mrr');
                }}
              >
                {t('finance.tables.topDays.sortByMrr')}
              </Button>
              <Button
                variant={sortKey === 'sales' ? 'primary' : 'normal'}
                onClick={(): void => {
                  setSortKey('sales');
                }}
              >
                {t('finance.tables.topDays.sortBySales')}
              </Button>
            </SpaceBetween>
          }
        >
          {t('finance.tables.topDays.title', { count: TOP_LIMIT })}
        </Header>
      }
      columnDefinitions={[
        {
          id: 'date',
          header: t('finance.tables.topDays.date'),
          cell: (row): React.ReactNode => formatYyyymmddDisplay(row.yyyymmdd),
        },
        {
          id: 'mrr',
          header: t('finance.tables.topDays.mrr'),
          cell: (row): React.ReactNode => formatUsdCents(row.mrr.usdEquivalentCents),
        },
        {
          id: 'sales',
          header: t('finance.tables.topDays.sales'),
          cell: (row): React.ReactNode => formatInt(row.tenants.createdToday),
        },
        {
          id: 'netNew',
          header: t('finance.tables.topDays.netNew'),
          cell: (row): React.ReactNode => formatUsdCents(row.netNewMrr.netUsdCents),
        },
      ]}
    />
  );
}
